import { Box, useMediaQuery } from '@mui/material'

import { DATASET_FOR_UI_CONFIG_REFRESH_ON_REAL_TIME_OPTIMIZE } from 'api/dataset/dataset.api'
import { useElplanInfo } from 'api/elplan/elplan.api'
import { OptJobType } from 'api/optJobs/optJobs.api'
import { useGetUserCollaboratorContextType, useHasPermission } from 'helpers/global.helper/global.helper'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import globalState from 'store/global/global'
import { resetZoom, resetZoomOnAllRoutes } from 'store/zoom/zoom'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import SystemMessageBanner from 'ui/components/SystemMessageBanner/SystemMessageBanner'
import Tour from 'ui/components/Tour/Tour'
import View from 'ui/components/View/View'
import BadDataBanner from 'ui/molecules/BadDataBanner/BadDataBanner'
import QueueProgressBar from 'ui/molecules/QueueProgressBar/QueueProgressBar'
import UiConfig from 'ui/uiConfig/UiConfig'
import { systemFeatureFlagActive, useSystemFeatureFlagIsActive } from 'utils/systemUtils'
import { useSnapshot } from 'valtio'
import DeviationSettingsMenu from './components/DeviationSettingsMenu/DeviationSettingsMenu'
import OptimizeModuleHeader from './components/OptimizeModuleHeader/OptimizeModuleHeader'
import styles from './OptimizeModule.module.less'
import optimizeViewStore, { resetOptimizeViewStore } from './store/optimizeViewStore'
import { useDatasetRefreshToken } from './utils/hooks/useDatasetRefreshToken'
import { useOptimizeTimeRanges } from './utils/hooks/useOptimizeTimeRanges'
import { useOptimizeViewConfigs } from './utils/hooks/useOptimizeViewConfigs'
import { useOverrides } from './utils/hooks/useOverrides'
import { usePreviousRegularOptJobTime } from './utils/hooks/usePreviousRegularOptJobTime'
import optimizeTours from './utils/optimizeTours'

const MENU_WIDTH_IN_PERCENT = 15
const CONTENT_WIDTH_IN_PERCENT = 100 - MENU_WIDTH_IN_PERCENT
const CONTENT_WIDTH_MENU_OPEN_IN_PERCENT = CONTENT_WIDTH_IN_PERCENT - 2
type OptimizeModuleProps = {
  regularOptJobType: Extract<OptJobType, 'regular' | 'regular_wip'>
  moduleName: 'optimize' | 'optimizewip'
  title: string
}

export default function OptimizeModule({ title, regularOptJobType, moduleName }: OptimizeModuleProps): ReactElement {
  const [runTour, setRunTour] = useState<null | number>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)
  const headerRef = useRef<HTMLDivElement>(null)
  const isScreenSmall = useMediaQuery('(max-width:1024px)')
  const datasetRefreshToken = useDatasetRefreshToken(regularOptJobType)
  // Only subscribe to the specific store properties we need to avoid unnecessary re-renders
  const activeSystemId = useSnapshot(optimizeViewStore).activeSystemId
  const navigationIsOpen = useSnapshot(globalState).navigationIsOpen
  const userCollaboratorContextType = useGetUserCollaboratorContextType()
  const { activeSystem } = useAuth()
  const isRegular = activeSystem?.tier === `regular`
  const showDeviationSettingsMenu = activeSystem?.primary_digital_twin?.uid ?? false
  const isActiveDraftProductionPlan = useSystemFeatureFlagIsActive('use_draftproductionplan')
  const showOptimizationComponents = !(isActiveDraftProductionPlan && moduleName === 'optimize')
  const showPublishedOptimizationWarningBox = useSystemFeatureFlagIsActive('use_PublishedOptimizationWarningBox')

  if (activeSystem?.id !== activeSystemId) {
    // Make sure that the store has valid values for the system
    resetOptimizeViewStore(activeSystem)
    resetZoom()
  }

  const handleMenuToggle = useCallback((isOpen: boolean): void => {
    setIsMenuOpen(isOpen)
  }, [])

  const comparePlansFeatureFlag = systemFeatureFlagActive(activeSystem, 'compare_production_plans')
  const previousRegularOptJobTime = usePreviousRegularOptJobTime(regularOptJobType, comparePlansFeatureFlag)
  const { datasetStartTime, datasetEndTime, productionPlanStartTime, measEndTime } = useOptimizeTimeRanges()

  const overrides = useOverrides({
    regularOptJobType,
    comparePlansFeatureFlag,
    previousRegularOptJobTime,
    datasetStartTime,
    datasetEndTime,
    productionPlanStartTime,
    measEndTime,
  })

  const { optimizeViewDefault } = useOptimizeViewConfigs({
    isRegular,
    datasetRefreshToken,
    overrides,
  })

  // To create data for compare bidplan
  const fetchElplanValuesForOptimize = systemFeatureFlagActive(activeSystem, 'electricity_plan_in_optimize_view')
  const hasElplanPermission = useHasPermission('view_electricity_trading_module')
  useElplanInfo(activeSystem?.id, fetchElplanValuesForOptimize && hasElplanPermission)

  useEffect(() => {
    if (!isRegular) {
      // Light-systems should always have default zoom
      resetZoomOnAllRoutes()
    }
  }, [isRegular])

  useEffect(() => {
    if (headerRef.current) {
      // +60 to account for the padding of the header, otherwise the settings list will be cut off
      setHeaderHeight(headerRef.current.offsetHeight + 60)
    }
  }, [])

  // Memoize the Tour setRunState callback to prevent re-renders
  const handleSetRunState = useCallback((state: boolean) => setRunTour(state ? 0 : null), [])

  // Memoize the Tour steps to prevent re-calculation
  const tourSteps = useMemo(() => optimizeTours.getTourSteps(runTour), [runTour])

  // Memoize the Box sx prop to prevent object recreation on each render
  const boxSx = useMemo(
    () => ({
      width:
        !showDeviationSettingsMenu || !isMenuOpen || isScreenSmall
          ? '100%'
          : navigationIsOpen
            ? `${CONTENT_WIDTH_MENU_OPEN_IN_PERCENT}%`
            : `${CONTENT_WIDTH_IN_PERCENT}%`,
    }),
    [showDeviationSettingsMenu, isMenuOpen, isScreenSmall, navigationIsOpen]
  )

  // Memoize header element to prevent re-renders
  const headerElement = useMemo(
    () => (
      <div ref={headerRef}>
        <OptimizeModuleHeader
          title={title}
          optJobType={regularOptJobType}
          showOptimizeButton={showOptimizationComponents}
          showAutomaticOptimizationToggleButton={showOptimizationComponents}
          showProductionPlanOutOfDate={showOptimizationComponents}
          showAccumulatorLevels={showOptimizationComponents}
          showLatestPublishedOptimizationInfoBox={isActiveDraftProductionPlan}
          showPublishOptimizationButton={moduleName === 'optimizewip'}
          showLatestSubtypeOptimizationInfoBox={!isActiveDraftProductionPlan}
          showOptimizationFailedGuide={showOptimizationComponents}
          showPublishedOptimizationWarningBox={showPublishedOptimizationWarningBox}
        />
      </div>
    ),
    [
      title,
      regularOptJobType,
      showOptimizationComponents,
      isActiveDraftProductionPlan,
      moduleName,
      showPublishedOptimizationWarningBox,
    ]
  )

  return (
    <View header={headerElement} stickyHeader>
      <SystemMessageBanner module={moduleName} />

      <Tour isSingle={!!runTour} run={runTour !== null} setRunState={handleSetRunState} steps={tourSteps} />

      <div className={styles.OptimizeModule__Content}>
        <Box sx={boxSx}>
          <BadDataBanner isVisible={showOptimizationComponents} />

          <UiConfig
            type="real_time_optimization_bars"
            datasetRefreshToken={DATASET_FOR_UI_CONFIG_REFRESH_ON_REAL_TIME_OPTIMIZE}
          />

          {/* Default Optimize View */}
          {userCollaboratorContextType === undefined && optimizeViewDefault}

          {/* Default Optimize for Collaborator A */}
          {userCollaboratorContextType === 'collaborator_a' && (
            <UiConfig
              type="optimize_view_variant_a"
              datasetRefreshToken={datasetRefreshToken}
              {...overrides}
              fallback={optimizeViewDefault}
            />
          )}

          {/* Default Optimize for Collaborator B */}
          {userCollaboratorContextType === 'collaborator_b' && (
            <UiConfig
              type="optimize_view_variant_b"
              datasetRefreshToken={datasetRefreshToken}
              {...overrides}
              fallback={optimizeViewDefault}
            />
          )}
        </Box>

        {showDeviationSettingsMenu && (
          <DeviationSettingsMenu
            menuWidth={MENU_WIDTH_IN_PERCENT}
            headerHeight={headerHeight}
            onToggle={handleMenuToggle}
          />
        )}
      </div>
      <QueueProgressBar />
    </View>
  )
}
